import React from 'react';
import { formatPhone, inp, lbl, noAutofill, row2, smallBtn } from './shared';
import { agentBox, agentTitle, ConsentSignature, copy, list } from './Consent';

// Oregon residents sign the Explore Health Unified Authorization to Assist
// (OHA form HIM5401) instead of the generic FFM consent - see
// TI_Forms_Certification's create_indfam_consent_or_pdf, which renders the
// signed copy. Section 4 needs the applicant's DOB and the other adults on
// the application, which the generic flow doesn't collect until Household -
// so this step writes them straight into householdMembers (the applicant as
// relationship Self, the others flagged addedOnConsent). Household then
// starts with them already listed instead of asking twice.

export function ageOn(dob, asOf = new Date()) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dob || ''));
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number);
  const age = asOf.getFullYear() - year;
  const hadBirthday = asOf.getMonth() + 1 > month || (asOf.getMonth() + 1 === month && asOf.getDate() >= day);
  return hadBirthday ? age : age - 1;
}

function applicantOf(members) {
  return members.find(m => m.relationship === 'Self');
}

function otherAdultsOf(members) {
  return members.filter(m => m.addedOnConsent);
}

export function countAdults19Plus(members) {
  const applicant = applicantOf(members);
  const applicantAge = ageOn(applicant?.dob);
  const others = otherAdultsOf(members).filter(m => (ageOn(m.dob) ?? 0) >= 19).length;
  return others + (applicantAge === null || applicantAge >= 19 ? 1 : 0);
}

// The count defaults to the adults listed above, but the client can raise it:
// the form asks about the (tax) household, which can include 19+ members who
// aren't applying - e.g. a dependent student covered elsewhere. Never below
// the listed adults, even if more get added after a count was picked.
const MAX_ADULTS_19_PLUS = 8;

export function adults19PlusValue(data) {
  const listed = countAdults19Plus(data.householdMembers || []);
  const entered = Number(data.orHouseholdAdults19Plus);
  return String(Number.isInteger(entered) && entered > listed ? entered : listed);
}

function adults19PlusOptions(members) {
  const listed = countAdults19Plus(members);
  const max = Math.max(MAX_ADULTS_19_PLUS, listed);
  return Array.from({ length: max - listed + 1 }, (_, i) => listed + i);
}

export function validateOregonConsent(data) {
  const members = data.householdMembers || [];
  const applicant = applicantOf(members);
  if (!String(data.firstName || '').trim() || !String(data.lastName || '').trim()) return 'Please enter your first and last name.';
  if (!String(data.email || '').trim()) return 'Please enter your email address.';
  if (!applicant?.dob) return 'Please enter your date of birth.';
  for (const [i, adult] of otherAdultsOf(members).entries()) {
    const label = `Other adult ${i + 1}`;
    if (!String(adult.firstName || '').trim() || !String(adult.lastName || '').trim()) return `${label}: please enter a first and last name.`;
    if (!adult.dob) return `${label}: please enter a date of birth.`;
    if ((ageOn(adult.dob) ?? 0) < 19) return `${label} is under 19. Only list adults here - children are added on the Household step.`;
  }
  return '';
}

export default function ConsentOregon({ data, onChange }) {
  const members = data.householdMembers || [];
  const applicant = applicantOf(members);
  const otherAdults = otherAdultsOf(members);

  function setApplicantDob(dob) {
    if (applicant) {
      onChange('householdMembers', members.map(m => m === applicant ? { ...m, dob } : m));
    } else {
      // seededFromBasicInfo keeps Household's name-sync behavior for this row.
      const self = { firstName: data.firstName || '', lastName: data.lastName || '', dob, gender: '', tobacco: '', pregnant: '', relationship: 'Self', seededFromBasicInfo: true };
      onChange('householdMembers', [self, ...members]);
    }
  }

  function updateAdult(adult, field, value) {
    onChange('householdMembers', members.map(m => m === adult ? { ...m, [field]: value } : m));
  }

  function addAdult() {
    onChange('householdMembers', [...members, { firstName: '', lastName: '', dob: '', gender: '', tobacco: '', pregnant: '', relationship: '', addedOnConsent: true }]);
  }

  function removeAdult(adult) {
    onChange('householdMembers', members.filter(m => m !== adult));
  }

  return (
    <div>
      <div style={introNote}>
        <strong>You only need to fill in two things:</strong> your information in Section 4 and your signature in
        Section 9 (both highlighted in yellow). Everything else is already filled in or is for you to read.
      </div>

      <div style={formatsBox}>
        <strong style={{ color: '#1a3364' }}>Other formats and languages</strong><br />
        You can get this document in other languages, large print, braille or a format you prefer free of charge.
        Contact Explore Health at explorehealth@oha.oregon.gov or 855-268-3767 (voice). We accept all relay calls.
      </div>

      <h4 style={docTitle}>Explore Health Unified Authorization to Assist</h4>

      <div style={notForYou}>
        <h5 style={section}>Section 1: Type of Enrollment Partner Authorized</h5>
        <label style={partnerRow}>
          <input type="checkbox" checked readOnly disabled style={partnerBox} />
          <span><strong>Insurance Agent</strong><br />A licensed insurance agent is authorized to recommend plans, provide guidance, and assist consumers with applying for, enrolling in, and maintaining coverage through Explore Health.</span>
        </label>
        <label style={{ ...partnerRow, opacity: 0.6 }}>
          <input type="checkbox" checked={false} readOnly disabled style={partnerBox} />
          <span><strong>Assister/Community Partner</strong><br />A certified assister or community partner organization is authorized to provide unbiased application and enrollment assistance for Oregon Health Plan (OHP) programs, qualified health plans (QHPs), and related affordability programs. Assisters may not recommend plans.</span>
        </label>
      </div>

      {/* Comes from the invite and goes on the signed PDF as-is - the client
          can't change who they're authorizing. */}
      <div style={agentBox}>
        <div style={agentTitle}>Section 2: Insurance Agent Information</div>
        <p style={lockedNote}>Filled in by your agent. You don't need to change anything here.</p>
        <div style={row2}>
          <LockedField label="Name of Insurance Agency" value={data.agencyName || 'Translating Insurance'} />
          <LockedField label="Agency Owner" value={data.agencyOwner || 'Jacquie Walter'} />
        </div>
        <div style={row2}>
          <LockedField label="Owner Phone" value={data.agencyPhone || '(503) 324-4511'} />
          <LockedField label="Owner Email Address" value={data.agencyEmail || 'support@translatinginsurance.com'} />
        </div>
        <div style={row2}>
          <LockedField label="Name of Primary Writing Agent" value={data.primaryWritingAgent || 'Jacquie Walter'} />
          <LockedField label="Agent National Producer Number (NPN)" value={data.agentNpn || ''} />
        </div>
        <div style={row2}>
          <LockedField label="Agent Phone" value={data.agentPhone || '(503) 324-4511'} />
          <LockedField label="Agent Email Address" value={data.agentEmail || 'support@translatinginsurance.com'} />
        </div>
      </div>

      <div style={notForYou}>
        <h5 style={section}>Section 3: Assister/Community Partner Information</h5>
        <p style={{ ...copy, fontStyle: 'italic' }}>Not applicable - your Enrollment Partner is an insurance agent.</p>
      </div>

      <div style={yourPart}>
        <div style={{ ...yourPartTag, marginTop: 0 }}>Your part (1 of 2)</div>
        <h5 style={{ ...section, marginTop: 4 }}>Section 4: Applicant Information</h5>
        <div style={{ ...grid3, marginBottom: 14 }}>
          <div><label style={lbl}>First Name<Req /></label><input style={inp} value={data.firstName || ''} onChange={e => onChange('firstName', e.target.value)} {...noAutofill('or-first-name')} /></div>
          <div><label style={lbl}>Middle Name</label><input style={inp} value={data.middleName || ''} onChange={e => onChange('middleName', e.target.value)} {...noAutofill('or-middle-name')} /></div>
          <div><label style={lbl}>Last Name<Req /></label><input style={inp} value={data.lastName || ''} onChange={e => onChange('lastName', e.target.value)} {...noAutofill('or-last-name')} /></div>
        </div>
        <div style={row2}>
          <div>
            <label style={lbl}>Primary Applicant Date of Birth<Req /></label>
            <input data-testid="applicantDob" style={inp} type="date" value={applicant?.dob || ''} onChange={e => setApplicantDob(e.target.value)} />
          </div>
          <div>
            <label style={lbl}>Primary Applicant Phone</label>
            <input style={inp} type="tel" value={data.phone || ''} onChange={e => onChange('phone', formatPhone(e.target.value))} {...noAutofill('or-phone')} />
          </div>
        </div>
        <div style={{ marginBottom: 14 }}>
          <label style={lbl}>Primary Applicant Email Address<Req /></label>
          <input style={inp} type="email" value={data.email || ''} onChange={e => onChange('email', e.target.value)} {...noAutofill('or-email')} />
        </div>

        <label style={lbl}>Other Adults (19 and over) on the Application</label>
        <p style={{ ...copy, fontSize: 13 }}>List anyone else 19 or older applying with you. Children are added later on the Household step.</p>
        {otherAdults.map((adult, i) => (
          <div key={i} style={adultCard}>
            <div style={adultHead}>
              <strong>Adult {i + 1}</strong>
              <button type="button" style={smallBtn} onClick={() => removeAdult(adult)}>Remove</button>
            </div>
            <div style={grid3}>
              <div><label style={lbl}>First Name<Req /></label><input style={inp} value={adult.firstName} onChange={e => updateAdult(adult, 'firstName', e.target.value)} {...noAutofill(`or-adult-${i}-first-name`)} /></div>
              <div><label style={lbl}>Last Name<Req /></label><input style={inp} value={adult.lastName} onChange={e => updateAdult(adult, 'lastName', e.target.value)} {...noAutofill(`or-adult-${i}-last-name`)} /></div>
              <div><label style={lbl}>Date of Birth<Req /></label><input style={inp} type="date" value={adult.dob} onChange={e => updateAdult(adult, 'dob', e.target.value)} /></div>
            </div>
          </div>
        ))}
        <button type="button" style={{ ...smallBtn, marginBottom: 14 }} onClick={addAdult}>Add Adult</button>

        <div>
          <label style={lbl}>Number of Household Members 19 and Over</label>
        <select
          data-testid="adults19Plus"
          style={{ ...inp, maxWidth: 120 }}
          value={adults19PlusValue(data)}
          onChange={e => onChange('orHouseholdAdults19Plus', e.target.value)}
        >
          {adults19PlusOptions(members).map(n => <option key={n} value={String(n)}>{n}</option>)}
        </select>
        <p style={{ ...copy, fontSize: 13, marginTop: 6 }}>
          Include anyone 19 or older in your tax household who isn't applying for coverage - for example, a student
          you claim as a dependent.
        </p>
        </div>
      </div>

      <h5 style={section}>Section 5: Scope of Authorization</h5>
      <p style={copy}>
        I give my permission to the Enrollment Partner listed above to serve as the Enrollment Partner for myself and
        my entire household, if applicable, for purposes of applying for and enrollment in coverage offered through
        Explore Health, Oregon’s health insurance marketplace. By consenting to this agreement, I authorize the
        above-mentioned Enrollment Partner to view and use the confidential information provided by me in writing,
        electronically, in person, or by telephone for the purposes of only one or more of the following:
      </p>
      <ul style={list}>
        <li>Searching for an existing Explore Health application;</li>
        <li>Completing an application for eligibility and enrollment in an Explore Health qualified health or dental plan or other government insurance affordability programs, such as Oregon Health Plan (Medicaid/Children’s Health Insurance Program (CHIP)/Basic Health Program), premium tax credits, or cost-sharing reductions to help pay for health insurance costs;</li>
        <li>Providing ongoing account maintenance and enrollment assistance, as necessary; or</li>
        <li>Responding to inquiries from Explore Health regarding my Explore Health application or enrollment.</li>
      </ul>

      <h6 style={subsection}>OHP Assistance (Assisters only)</h6>
      <p style={copy}>
        I authorize Oregon Health Authority (OHA), Oregon Department of Human Services (ODHS), and Explore Health to
        share the following information with my community partner organization and assister:
      </p>
      <ul style={list}>
        <li>My application</li>
        <li>Enrollment details</li>
        <li>Enrollment status</li>
        <li>Plan benefits</li>
        <li>Protected health information (PHI)</li>
      </ul>
      <p style={copy}>I understand that my Enrollment partner may not ask about my immigration status or the members of my household or share any information about my application unless authorized by me.</p>
      <p style={copy}>I understand assisters/community partners may not recommend a coordinated care organization (CCO) or any health insurance plan.</p>
      <p style={copy}>I understand these organizations must protect my information and may not share information about mental health, HIV/AIDS, drug or alcohol treatment, or genetic tests without additional authorization.</p>

      <h5 style={section}>Section 6: Privacy and Security</h5>
      <p style={copy}>I understand the Enrollment Partner must:</p>
      <ul style={list}>
        <li>Use my personally identifiable information (PII) or personal health information (PHI) <strong>only</strong> for authorized assistance purposes;</li>
        <li>Protect my information from unauthorized access, use, or disclosure;</li>
        <li>Collect, use, and disclose only the minimum necessary information;</li>
        <li>Maintain secure storage and transmission of my information;</li>
        <li>Follow all privacy rules required by Explore Health, OHA, and ODHS.</li>
      </ul>
      <p style={copy}>I understand I do not have to share additional personal information with my Enrollment Partner beyond what is required for completing my application.</p>

      <h5 style={section}>Section 7: Applicant Responsibilities</h5>
      <p style={copy}>I understand that:</p>
      <ul style={list}>
        <li>I must provide true and correct information on my application.</li>
        <li>I must respond to notices of missing or incorrect information when asked.</li>
        <li>I may receive help or referrals to assistance in a language I speak or prefer.</li>
      </ul>

      <h5 style={section}>Section 8: Revoking or Ending Consent</h5>
      <p style={copy}>
        Canceling authorization does not apply to information already shared. My authorization remains valid until I
        notify OHA/ODHS and Explore Health that I no longer wish to work with this Enrollment Partner or until I request
        assistance from another Enrollment Partner.
      </p>
      <h6 style={subsection}>If my Enrollment Partner is an Insurance Agent:</h6>
      <p style={copy}>
        I may revoke or modify my consent at any time by contacting the agent, agency, or agency owner listed above. I
        may also log in to my Applicant Dashboard and revoke consent or contact Explore Health by calling 855-268-3767
        (relay calls welcome) Monday to Friday 8 a.m. to 5 p.m. PST.
      </p>
      <h6 style={subsection}>If my Enrollment Partner is an Assister/Community Partner:</h6>
      <p style={copy}>
        I may revoke my authorization by logging in to my Applicant Dashboard or contacting Explore Health by calling
        855-268-3767 (relay calls welcome) Monday to Friday 8 a.m. to 5 p.m. PST. I must also notify OHA/ODHS if I wish
        to revoke consent from my assister/community partner by calling 800-699-9075 (relay calls welcome) or by
        emailing Oregon.Benefits@odhsoha.oregon.gov.
      </p>

      <div style={yourPartTag}>Your part (2 of 2)</div>
      <h5 style={{ ...section, marginTop: 4 }}>Section 9: Signature<Req /></h5>
      <ConsentSignature data={data} onChange={onChange} />
    </div>
  );
}

function Req() {
  return <span style={{ color: '#c0392b', marginLeft: 3 }}>*</span>;
}

function LockedField({ label, value }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={lbl}>{label}</label>
      <input style={lockedInp} value={value || ''} readOnly tabIndex={-1} />
    </div>
  );
}

// Grey = the agent's info, which the client can't change. Yellow matches
// ConsentSignature's box so both of the client's parts read as one kind.
const lockedInp = { ...inp, background: '#eef1f5', color: '#6b7a90', borderColor: '#d8dee6', cursor: 'not-allowed' };
const lockedNote = { fontSize: 13, color: '#6b7a90', fontStyle: 'italic', margin: '-4px 0 12px' };
const introNote = { background: '#fff8e8', border: '1.5px solid #d89b2b', borderRadius: 8, padding: '12px 14px', fontSize: 14, lineHeight: 1.5, color: '#1a3364', marginBottom: 16 };
const notForYou = { opacity: 0.6 };
const yourPart = { background: '#fff8e8', border: '2px solid #d89b2b', borderRadius: 10, padding: '14px 18px 18px', margin: '22px 0', boxShadow: '0 8px 24px rgba(26, 51, 100, 0.08)' };
const yourPartTag = { display: 'inline-block', background: '#d89b2b', color: '#fff', fontSize: 12, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', borderRadius: 4, padding: '3px 8px', marginTop: 22 };
const formatsBox = { border: '1px solid #c5d5e8', borderRadius: 6, padding: '10px 12px', fontSize: 13, lineHeight: 1.5, color: '#4a6288', marginBottom: 16 };
const docTitle = { color: '#1a3364', fontSize: 20, fontWeight: 800, margin: '0 0 14px' };
const section = { color: '#1a3364', fontSize: 16, fontWeight: 800, margin: '22px 0 10px' };
const subsection = { color: '#1a3364', fontSize: 14, fontWeight: 800, margin: '14px 0 6px' };
const partnerRow = { display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 14, lineHeight: 1.5, color: '#4a6288', marginBottom: 12 };
const partnerBox = { width: 18, height: 18, marginTop: 2, accentColor: '#1a3364', flex: '0 0 auto' };
const adultCard = { border: '1.5px solid #c5d5e8', borderRadius: 8, padding: 12, marginBottom: 12 };
const adultHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, color: '#1a3364' };
const grid3 = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 };
